import {
	Button,
	Dialog,
	FormattedText,
	IconButton,
	Toolbar,
	useConfirmationDialog
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useRef,
	useState
} from "react";
import { TemplateList } from "./TemplateList";
import styles from "./ChooseHubTemplateDialog.module.less";
import { HubItemKind } from "client/Api/Hub/hubItemCategories";
import { useRenameTemplateDialog } from "../Management/HubManagementDialog/Content/Templates/RenameTemplateDialog";
import {
	maxNumberOfHubTemplates,
	useCreateHubTemplateDialog
} from "../Management/Templates/CreateHubTemplateDialog";
import { useEditHubTemplateDialog } from "../Management/Templates/EditHubTemplateDialog";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useForceRerender,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { resolveFormattedTextString } from "client/Misc/utils";
import { hasImplicitDependencyOn } from "client/Misc/reactUtils";
import { HubTemplateListModel, list } from "client/Api/Hub/Templates/hubTemplates";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { deleteTemplates } from "client/Api/Hub/Templates/hubTemplateManagement";
import { HubTemplatePreview } from "./HubTemplatePreview";

export type ChooseHubTemplateDialogProps = {
	kind: HubItemKind;
	onChoose(templateId?: number): void;
	onClose(): void;
	onBack?(): void;
};

export const ChooseHubTemplateDialog = (props: ChooseHubTemplateDialogProps): ReactElement => {
	const {
		kind,
		onChoose,
		onClose,
		onBack
	} = props;

	const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>();
	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [state, setState] = useState<State>({
		isLoading: true
	});

	const [reload, itemsVersion] = useForceRerender();

	const [deleteDialog, openDeleteDialog] = useConfirmationDialog();
	const [renameDialog, openRenameDialog] = useRenameTemplateDialog();
	const [createHubTemplateDialog, openCreateHubTemplateDialog] = useCreateHubTemplateDialog();
	const [editHubTemplateDialog, openEditHubTemplateDialog] = useEditHubTemplateDialog();

	const templateIdToSelectAfterLoadRef: RefObject<number | undefined> = useRef<number>(undefined);
	const alertManager: AlertManager = useAlerts();
	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const isPreviewAvailableInViewPort: boolean = useWorkspaceMediaQuery(MediaQueryKind.CompactTablet);

	useEffect(
		() => {
			hasImplicitDependencyOn(itemsVersion);

			async function fetchItems(signal: AbortSignal): Promise<void> {
				try {
					const response: HubTemplateListModel = await list(kind, signal);

					setState({
						isLoading: false,
						hasError: false,
						response
					});

					const templateIdToSelect: number | undefined = templateIdToSelectAfterLoadRef.current;

					if (templateIdToSelect === undefined) {
						return;
					}

					const hasRequestedTemplate: boolean = response.items
						.some(x => x.id === templateIdToSelect);

					if (!hasRequestedTemplate) {
						throw new Error(`There's no template for id ${templateIdToSelect}`);
					}

					setSelectedTemplateId(templateIdToSelect);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));

					setState({
						isLoading: false,
						hasError: true
					});
				}
			}

			const cancellation = new AbortController();
			fetchItems(cancellation.signal);

			return () => cancellation.abort();
		},
		[
			alertManager,
			kind,
			itemsVersion
		]
	);

	const createNewHandler = useCallback(
		async (): Promise<void> => {
			const templateCreation: TryResult<number> = await openCreateHubTemplateDialog({
				closeDialogOn: "back",
				kind,
				onClose: onClose
			});

			if (!templateCreation.wasSuccessful) {
				return;
			}

			templateIdToSelectAfterLoadRef.current = templateCreation.result;
			reload();
		},
		[
			kind,
			onClose,
			openCreateHubTemplateDialog,
			reload
		]
	);

	const editHandler = useCallback(
		async (id: number): Promise<void> => {
			const result: boolean = await openEditHubTemplateDialog({
				closeDialogOn: "back",
				onClose,
				templateId: id,
			});

			if (result) {
				reload();
			}

			setSelectedTemplateId(id);
		},
		[
			reload,
			onClose,
			openEditHubTemplateDialog
		]
	);

	const duplicateHandler = useCallback(
		async (id: number): Promise<void> => {
			const templateCreation: TryResult<number> = await openCreateHubTemplateDialog({
				closeDialogOn: "back",
				basedOn: {
					templateId: id
				},
				kind,
				onClose: onClose
			});

			if (!templateCreation.wasSuccessful) {
				return;
			}

			templateIdToSelectAfterLoadRef.current = templateCreation.result;
			reload();
		},
		[
			kind,
			onClose,
			openCreateHubTemplateDialog,
			reload
		]
	);

	const deleteHandler = useCallback(
		async (idToDelete: number): Promise<void> => {
			const result = await openDeleteDialog({
				confirmText: localize("_Delete"),
				title: localize("_AreYouSureYouWantToDeleteThisTemplate"),
				trackingId: "delete-template-dialog"
			});

			if (!result.wasSuccessful) {
				return;
			}

			try {
				setIsBusy(true);

				await deleteTemplates([idToDelete], globalCancellation);

				alertManager.showSuccess(localize("_SuccessfullyDeletedTheItems"));

				setSelectedTemplateId(x => {
					return x === idToDelete
						? null
						: x;
				});

				if (templateIdToSelectAfterLoadRef.current === idToDelete) {
					templateIdToSelectAfterLoadRef.current = undefined;
				}

				setIsBusy(false);
				reload();
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				console.error(e);
				alertManager.showError(localize("_GeneralErrorMessage"));
				setIsBusy(false);
			}
		},
		[
			openDeleteDialog,
			globalCancellation,
			alertManager,
			reload
		]
	);

	const renameHandler = useCallback(
		(
			id: number,
			currentName: string
		): void => {
			openRenameDialog({
				currentName,
				id
			}).then(() => {
				reload();
			});
		},
		[
			openRenameDialog,
			reload
		]
	);

	const renderList = (): ReactElement | null => {
		if (state.isLoading || state.hasError) {
			return null;
		}

		const {
			items,
			canManageTemplates
		} = state.response;

		return (
			<TemplateList
				itemKind={kind}
				items={items}
				canManageTemplates={canManageTemplates}
				selectedItemId={selectedTemplateId}
				onSelectedItemIdChange={setSelectedTemplateId}
				onChoose={onChoose}
				onEdit={editHandler}
				onDuplicate={duplicateHandler}
				onDelete={deleteHandler}
				onRename={renameHandler}
			/>
		);
	};

	const canManageTemplates: boolean = !state.isLoading && !state.hasError
		&& state.response.canManageTemplates;

	const canCreateMoreTemplates: boolean = !state.isLoading && !state.hasError
		&& state.response.items.length < maxNumberOfHubTemplates;

	const openedDialogElement: ReactElement | null = createHubTemplateDialog
		|| editHubTemplateDialog;

	const getMaximumNumberOfTemplatesMessage = (): string => {
		const sentenceWithTheProblem: string = resolveFormattedTextString({
			template: localize("_HubTemplatesMaxCountErrorMessage"),
			replacements: [
				{
					what: "{{MaxCount}}",
					filler: `${maxNumberOfHubTemplates}`
				}
			]
		});

		const sentenceWithTheSolution: string = localize("_RemoveExistingTemplatesToCreateNewOnes");

		return `${sentenceWithTheProblem} ${sentenceWithTheSolution}`;
	};

	if (openedDialogElement) {
		return openedDialogElement;
	}

	return (
		<>
			<Dialog
				trackingId={itemKindToTrackingIdMap[kind]}
				hasBusyIndicator={state.isLoading || isBusy}
				variant="left-slider"
				onClose={onClose}
			>
				<Dialog.Header
					hasBorder
					title={
						<FormattedText
							text={localize("_ChooseTemplateForItemTypeName")}
							thingsToReplace={[
								{
									what: "{{ItemTypeName}}",
									filler: localize(itemKindToLocalizationKeysMap[kind])
										.toLocaleLowerCase()
								}
							]}
						/>
					}
					onClose={onClose}
					onBack={onBack}
				/>
				<Dialog.Body goesEdgeToEdge>
					<div className={styles.container}>
						<div className={styles.content}>
							<div className={styles.header}>
								<h3 className={styles.title}>
									{localize("_Templates")}
								</h3>
								{canManageTemplates && (
									<div className={styles.headerButton}>
										{isResponsive
											? (
												<IconButton
													kind="secondary"
													icon="plus"
													onClick={createNewHandler}
													isDisabled={!canCreateMoreTemplates}
													tooltip={
														canCreateMoreTemplates
															? undefined
															: getMaximumNumberOfTemplatesMessage()
													}
													size="large"
													shape="round"
													trackingId="create-new-template"
												/>
											) : (
												<Button
													kind="secondary"
													text={localize("_NewTemplate")}
													icon="plus"
													onClick={createNewHandler}
													isDisabled={!canCreateMoreTemplates}
													tooltip={
														canCreateMoreTemplates
															? undefined
															: getMaximumNumberOfTemplatesMessage()
													}
													size="large"
													trackingId="create-new-template"
												/>
											)}

									</div>
								)}
							</div>
							<div className={styles.list}>
								{renderList()}
							</div>
						</div>
						{isPreviewAvailableInViewPort && (
							<div className={styles.preview}>
								<HubTemplatePreview
									trackingId="template-preview"
									kind={kind}
									templateId={selectedTemplateId}
								/>
							</div>
						)}
					</div>

				</Dialog.Body>
				<Dialog.Footer hasBorder>
					<Toolbar>
						<Toolbar.Button
							trackingId="next"
							text={localize("_Next")}
							onClick={() => onChoose(selectedTemplateId ?? undefined)}
							kind="primary"
						/>
					</Toolbar>
				</Dialog.Footer>
			</Dialog>
			{deleteDialog}
			{renameDialog}
		</>
	);
};
ChooseHubTemplateDialog.displayName = "ChooseHubTemplateDialog";

type State = {
	isLoading: true;
} | {
	isLoading: false;
	hasError: true;
} | {
	isLoading: false;
	hasError: false;
	response: HubTemplateListModel;
};

export const itemKindToLocalizationKeysMap: { [key in HubItemKind]: LocalizationKey } = {
	[HubItemKind.Announcement]: "_Announcement",
	[HubItemKind.KnowledgeItem]: "_KnowledgeArticle",
	[HubItemKind.Event]: "_Event"
};

const itemKindToTrackingIdMap: { [key in HubItemKind]: string } = {
	[HubItemKind.Announcement]: "choose-announcement-template-dialog",
	[HubItemKind.KnowledgeItem]: "choose-knowledge-item-template-dialog",
	[HubItemKind.Event]: "choose-event-template-dialog"
};
